import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  Skeleton,
  useToast,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@rumbledirect/ui-v2";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useCurrentUser } from "@/currentUser/CurrentUserProvider";
import { hasPermission } from "@/currentUser/permissions";
import type { Employee } from "@/employees/employee";
import { getEmployeeFullName } from "@/employees/employeeFields";
import { EmployeeFormSheet } from "@/employees/EmployeeFormSheet";
import { toEmployeePatchInput } from "@/employees/employeeForm";
import { DEFAULT_EMPLOYEES_SEARCH } from "@/employees/employeesSearch";
import { useDeleteEmployee } from "@/employees/useDeleteEmployee";
import { useUpdateEmployee } from "@/employees/useUpdateEmployee";
import { EmployeeAuditPanel } from "@/employees/EmployeeAuditPanel";
import { EmployeeActivityPanel } from "@/employees/EmployeeActivityPanel";
import { m } from "framer-motion";
import {
  useMotionInitial,
  useMotionTransition,
} from "@rumbledirect/ui-v2/motion";
import { fadeIn } from "@rumbledirect/ui-v2/motion/variants";
import { useGetAdminEmployeesEmployeeId } from "@/lib/api/generated/employee/employee";
import {
  EmployeeEmployeeResourceEmployeePostInputJsonEmploymentContractType as contractTypes,
  PersonOutputJsonldGender as genders,
} from "@/lib/api/generated/model";
import {
  EMPTY_VALUE,
  formatCentsAsEuros,
  toDateInputValue,
} from "@rumbledirect/ui-v2/format";

const EMPLOYEE_TABS = ["hr", "audit", "activity"] as const;
type EmployeeTab = (typeof EMPLOYEE_TABS)[number];

function toEmployeeTab(value: unknown): EmployeeTab {
  return EMPLOYEE_TABS.find((tab) => tab === value) ?? "hr";
}

export const Route = createFileRoute("/_authenticated/employees_/$employeeId")({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: toEmployeeTab(search.tab),
  }),
  component: EmployeeDetailPage,
});

function EmployeeDetailPage(): ReactNode {
  const { employeeId } = Route.useParams();
  const { tab } = Route.useSearch();
  const tabNavigate = Route.useNavigate();
  const initial = useMotionInitial("hidden");
  const transition = useMotionTransition({ duration: 0.2 });
  const navigate = useNavigate();
  const toast = useToast();
  const canUpdate = hasPermission(useCurrentUser(), "admin_employee_update");
  const { data, isPending, isError, refetch } =
    useGetAdminEmployeesEmployeeId(employeeId);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const updateMutation = useUpdateEmployee(employeeId);
  const deleteMutation = useDeleteEmployee();

  return (
    <div className="space-y-4">
      <Link
        to="/employees"
        search={DEFAULT_EMPLOYEES_SEARCH}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Terug naar medewerkers
      </Link>
      {isError ? (
        <Card>
          <CardContent className="space-y-3 py-10 text-center">
            <p role="alert">We konden deze medewerker niet laden.</p>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              Opnieuw proberen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {isPending || !data ? (
            <Skeleton className="h-9 w-64" />
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">
                {getEmployeeFullName(data)}
              </h1>
              <div className="ml-auto flex gap-2">
                {tab === "hr" && (
                  <RequirePermission permission="admin_employee_update">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditOpen(true)}
                    >
                      <Pencil className="size-4" aria-hidden="true" />
                      Bewerken
                    </Button>
                  </RequirePermission>
                )}
                {tab === "hr" && (
                  <RequirePermission permission="admin_employee_delete">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setDeleteOpen(true)}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                      Uitdiensttreding
                    </Button>
                  </RequirePermission>
                )}
              </div>
            </div>
          )}
          {data ? (
            <m.div
              variants={fadeIn}
              initial={initial}
              animate="visible"
              transition={transition}
            >
              {/* Agreed backend divergences: no provisions/dossier tabs, mobility rights, procurement, swap, exceptions, urgent actions or tasks. */}
              <Tabs
                value={tab}
                onValueChange={(value) =>
                  void tabNavigate({
                    replace: true,
                    search: {
                      tab: toEmployeeTab(value),
                    },
                  })
                }
                className="min-w-0 space-y-5"
              >
                <TabsList className="max-w-full self-start overflow-x-auto">
                  <TabsTrigger value="hr">HR-gegevens</TabsTrigger>
                  <TabsTrigger value="audit">Wijzigingslog</TabsTrigger>
                  <TabsTrigger value="activity">Activiteit</TabsTrigger>
                </TabsList>
                <TabsContent value="hr">
                  <EmployeeDetailBody employee={data} />
                </TabsContent>
                <TabsContent value="audit">
                  <RequirePermission
                    permission="admin_change_request_read"
                    fallback={
                      <p>Je hebt geen toegang tot het wijzigingslog.</p>
                    }
                  >
                    <EmployeeAuditPanel
                      employeeNumber={data.employeeNumber}
                      employeeName={getEmployeeFullName(data)}
                    />
                  </RequirePermission>
                </TabsContent>
                <TabsContent value="activity">
                  <RequirePermission
                    permission="admin_log_read"
                    fallback={
                      <p>Je hebt geen toegang tot het activiteitenlog.</p>
                    }
                  >
                    <EmployeeActivityPanel employeeId={employeeId} />
                  </RequirePermission>
                </TabsContent>
              </Tabs>
            </m.div>
          ) : (
            <EmployeeDetailBody employee={undefined} />
          )}
        </>
      )}
      {canUpdate && data && (
        <EmployeeFormSheet
          open={editOpen}
          onOpenChange={setEditOpen}
          employee={data}
          isSubmitting={updateMutation.isPending}
          onSubmit={(values) =>
            updateMutation.mutate(toEmployeePatchInput(values, data), {
              onSuccess: () => {
                setEditOpen(false);
                toast.success("Medewerker bijgewerkt.");
              },
            })
          }
        />
      )}
      <Dialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Uitdiensttreding verwerken"
        description={
          data
            ? `Uitdiensttreding voor ${getEmployeeFullName(data)} verwerken? Zonder bestaande einddatum wordt vandaag vastgelegd. De medewerker blijft bewaard.`
            : undefined
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleteMutation.mutate(employeeId, {
                  onSuccess: () => {
                    setDeleteOpen(false);
                    void navigate({
                      to: "/employees",
                      search: DEFAULT_EMPLOYEES_SEARCH,
                    });
                  },
                })
              }
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Verwerken
            </Button>
          </>
        }
      />
    </div>
  );
}

function DetailField({
  label,
  value,
}: {
  readonly label: string;
  readonly value: ReactNode;
}): ReactNode {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm text-foreground">{value || EMPTY_VALUE}</dd>
    </div>
  );
}

function EmployeeDetailBody({
  employee,
}: {
  readonly employee: Employee | undefined;
}): ReactNode {
  if (!employee)
    return (
      <Card>
        <CardContent
          className="space-y-3 py-6"
          role="status"
          aria-live="polite"
        >
          <span className="sr-only">Medewerker laden</span>
          {[1, 2, 3, 4].map((key) => (
            <Skeleton key={key} className="h-4 w-full max-w-md" />
          ))}
        </CardContent>
      </Card>
    );
  const person = employee.person;
  const addressFields = (address: Employee["address"]) => (
    <dl className="grid gap-4 sm:grid-cols-2">
      <DetailField
        label="Straat"
        value={[
          address?.street,
          address?.streetNumber,
          address?.streetNumberAddition,
        ]
          .filter(Boolean)
          .join(" ")}
      />
      <DetailField label="Postcode" value={address?.postalCode} />
      <DetailField label="Plaats" value={address?.city} />
      <DetailField label="Land" value={address?.country} />
    </dl>
  );
  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <CardTitle>Persoon</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <DetailField label="Initialen" value={person?.initials} />
            <DetailField label="Naam" value={getEmployeeFullName(employee)} />
            <DetailField label="E-mail" value={person?.email} />
            <DetailField
              label="Telefoon"
              value={
                person?.telephone?.rawInput ??
                (person?.telephone?.countryCode &&
                person?.telephone?.nationalNumber
                  ? `+${person.telephone.countryCode}${person.telephone.nationalNumber}`
                  : null)
              }
            />
            <DetailField
              label="Geslacht"
              value={
                person?.gender === genders.male
                  ? "Man"
                  : person?.gender === genders.female
                    ? "Vrouw"
                    : person?.gender === genders.other
                      ? "Anders"
                      : person?.gender
              }
            />
            <DetailField label="Taal" value={person?.language} />
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Adres en postadres</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="mb-3 font-medium">Adres</h3>
            {addressFields(employee.address)}
          </div>
          <div>
            <h3 className="mb-3 font-medium">Postadres</h3>
            {addressFields(employee.postalAddress)}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>HR</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <DetailField
              label="Medewerkernummer"
              value={employee.employeeNumber}
            />
            <DetailField label="Functie" value={employee.jobFunction} />
            <DetailField label="Afdeling" value={employee.department} />
            <DetailField label="Kostenplaats" value={employee.costCenter} />
            <DetailField label="Salariseenheid" value={employee.salaryUnit} />
            <DetailField
              label="Contracttype"
              value={
                employee.employmentContractType === contractTypes.permanent
                  ? "Vast"
                  : employee.employmentContractType === contractTypes.temporary
                    ? "Tijdelijk"
                    : employee.employmentContractType === contractTypes.external
                      ? "Extern"
                      : employee.employmentContractType
              }
            />
            <DetailField label="Uren per week" value={employee.hoursPerWeek} />
            <DetailField
              label="Datum indiensttreding"
              value={toDateInputValue(employee.employmentDateAt)}
            />
            <DetailField
              label="Datum uitdiensttreding"
              value={toDateInputValue(employee.terminationDateAt)}
            />
            <DetailField
              label="Actief"
              value={employee.active ? "Ja" : "Nee"}
            />
            <DetailField
              label="Leasebudget"
              value={formatCentsAsEuros(employee.leaseBudget)}
            />
            <DetailField label="Rechtspersoon" value={employee.legalEntity} />
            <DetailField label="Werknemertype" value={employee.workerType} />
            <DetailField
              label="Leidinggevende"
              value={
                employee.reportsTo?.id ? (
                  <Link
                    to="/employees/$employeeId"
                    params={{ employeeId: employee.reportsTo.id }}
                    search={{ tab: "hr" }}
                    className="text-primary hover:underline"
                  >
                    {employee.reportsTo.name ||
                      employee.reportsTo.employeeNumber ||
                      employee.reportsTo.id}
                  </Link>
                ) : null
              }
            />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
